import React, { useEffect, useState, useRef } from "react";
import Header from "../header/Header";
import Sidebar from "../sidebar/Sidebar";
import Icons from "@/core/assets/svg/icons.svg";
import "./Layout.scss";
import { useUnit } from "effector-react";
import { $appStore, setIsMobileNavOpen } from "@/core/store/app";
import { useLocation, useNavigate } from "react-router-dom";
import { $userBasicInfo } from "@/features/user/store";
import { ROUTES } from "@/core/constants/app";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID } from "@/core/constants/env";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { imageList } from "@/core/utils/imageList";
import { MobileMenu } from "../header/components/MobileMenu";
import { SEO } from "@/core/components/SEO/SEO";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const appStore = useUnit($appStore);
  const location = useLocation();
  const navigate = useNavigate();
  const userBasicInfo = useUnit($userBasicInfo);

  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const helpButtonRef = useRef<HTMLDivElement>(null);

  const isChatPage = location.pathname.startsWith("/messages");

  // Close popup when clicking outside (only if no modal is open)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close help popup if a modal is open
      if (appStore.modalIsOpened) {
        return;
      }

      // Don't close if clicking inside the help popup itself
      const helpPopup = document.querySelector(".help-popup");
      if (helpPopup && helpPopup.contains(target)) {
        return;
      }

      // Only close if clicking outside the help button and help popup
      if (helpButtonRef.current && !helpButtonRef.current.contains(target)) {
        setShowHelpPopup(false);
      }
    };

    if (showHelpPopup && !appStore.modalIsOpened) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelpPopup, appStore.modalIsOpened]);

  // Redirect new users to account setup (but not if already on account setup page)
  useEffect(() => {
    if (
      userBasicInfo &&
      userBasicInfo.is_new &&
      location.pathname !== ROUTES.ACCOUNT_SETUP
    ) {
      navigate(ROUTES.ACCOUNT_SETUP, { replace: true });
    } else if (
      userBasicInfo &&
      !userBasicInfo.is_new &&
      location.pathname === ROUTES.ACCOUNT_SETUP
    ) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [userBasicInfo, location.pathname, navigate]);

  useEffect(() => {
    const appLayout = document.querySelector(".app-layout") as HTMLElement;
    if (appLayout) {
      if (location.pathname.includes("idea-edit")) {
        appLayout.style.background = "#fff";
      } else {
        appLayout.style.background = "";
      }
    }

    // Cleanup function to reset background when component unmounts
    return () => {
      if (appLayout) {
        appLayout.style.backgroundColor = "";
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileNavOpen(false);
  }, [location.pathname]);
  return (
    <>
      <GoogleOAuthProvider clientId={VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID}>
        {appStore.isMobileNavOpen && <MobileMenu />}
        {location.pathname === "/" && (
          <SEO
            title={"EU Funding & Partners Network | EUcalls.net | EUcalls"}
            description={
              "EUcalls helps you achieve your EU funding and partnerships goals! Find the latest EU calls for European Union funding and build your consortium."
            }
          />
        )}
        <div
          className={`app-layout ${location.pathname.includes("/settings") && "settings-page-layout"} ${isChatPage ? "app-layout--chat" : ""}`}
        >
          <Header />
          <div className="app-layout__container">
            <Sidebar />
            <div className="app-layout__main">{children}</div>
          </div>
          {!isChatPage && (
            <div className="app-layout__footer">
              © {new Date().getFullYear()} EUcalls. All rights reserved. Powered
              by{" "}
              <a href="https://joistpark.eu/" target="_blank">
                JOIST
              </a>
            </div>
          )}
          <div
            className="help"
            ref={helpButtonRef}
            onClick={() => {
              setShowHelpPopup(!showHelpPopup);
            }}
          >
            <img src={imageList.help} />
          </div>

          {/* Help Center Popup */}
          {showHelpPopup && (
            <>
              <div className="help-popup-overlay" />
              <div className="help-popup">
                <div className="help-popup-content">
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open("https://eucalls.net/help-center/", "_blank");
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <span>Help center</span>
                  </div>
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open("https://eucalls.net/contact/", "_blank");
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <span>Contact us</span>
                  </div>
                  <div className="divider" />
                  <div className="help-header">
                    <span>Follow us</span>
                  </div>
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        "https://www.linkedin.com/in/eucalls-funding-249b82158/",
                        "_blank",
                      );
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <SvgIcon icon="icon-linkedin" />
                    <span>LinkedIn</span>
                  </div>
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        "https://www.facebook.com/EUcalls/",
                        "_blank",
                      );
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <SvgIcon icon="icon-facebook" />
                    <span>Facebook</span>
                  </div>
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        "https://www.instagram.com/eucallsnet",
                        "_blank",
                      );
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <SvgIcon icon="icon-instagram" />
                    <span>Instagram</span>
                  </div>

                  <div className="divider" />
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open("https://eucalls.net/", "_blank");
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <SvgIcon icon="icon-eucalls" />
                    <span>EUcalls.net</span>
                  </div>
                  <div className="divider" />
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        "https://eucalls.net/privacy-policy",
                        "_blank",
                      );
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <span>Privacy Policy</span>
                  </div>
                  <div
                    className="help-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        "https://eucalls.net/terms-of-service",
                        "_blank",
                      );
                      if (!appStore.modalIsOpened) {
                        setShowHelpPopup(false);
                      }
                    }}
                  >
                    <span>Terms & Conditions</span>
                  </div>
                  <div className="help-footer">
                    <span>
                      © {new Date().getFullYear()}. All rights reserved
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <Icons />
      </GoogleOAuthProvider>
    </>
  );
};

export default Layout;
